import { useState, useEffect } from "react";
import { useAuth, authenticateUser } from './context/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';

function LoginForm({ onLogin }) {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberPassword: false,
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    const rememberedPassword = localStorage.getItem("rememberedPassword");
  
    if (rememberedEmail && rememberedPassword) {
      setFormData({
        email: rememberedEmail,
        password: rememberedPassword,
        rememberPassword: true,
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }

    if (serverError) setServerError("");
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleRememberPassword = () => {
    setFormData({
      ...formData,
      rememberPassword: !formData.rememberPassword,
    });
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", password: "" };

    if (!formData.email) {
      newErrors.email = "O e-mail é obrigatório";
      valid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Formato de e-mail inválido";
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = "A senha é obrigatória";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const result = await authenticateUser(formData.email, formData.password);

      if (result.token && result.agencyId) {
        login(result.token, result.agencyId);

        if (formData.rememberPassword) {
          localStorage.setItem("rememberedEmail", formData.email);
          localStorage.setItem("rememberedPassword", formData.password);
        } else {
          localStorage.removeItem("rememberedEmail");
          localStorage.removeItem("rememberedPassword");
        }

        navigate('/team');
      } else {
        setServerError('Usuário ou senha inválidos');
      }
    } catch (error) {
      console.error(error);
      setServerError(error.message || "Erro de autenticação.");
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.email && formData.password && !errors.email && !errors.password;

  return (
    <form className="login-form-wrapper" onSubmit={handleSubmit}>
      <div className="form-header">
        <div className="form-title">Faça login na sua conta</div>
        <div className="form-subtitle">
          Entre com seu e-mail e senha para continuar
        </div>
      </div>

      <div className="form-content">
        <div className="form-fields">
          <div className="form-field">
            <div className="field-label">E-mail:</div>
            <div className={`input-container ${errors.email ? "input-error" : ""}`}>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Digite seu melhor e-mail"
                className="form-input"
              />
            </div>
            {errors.email && <div className="error-message">{errors.email}</div>}
          </div>

          <div className="form-field">
            <div className="field-header">
              <div className="field-label">Senha:</div>
              <button
                type="button"
                className="forgot-password"
                onClick={() => navigate("/forgot-password")}
              >
                Esqueceu sua senha?
              </button>
            </div>
            <div className={`input-container ${errors.password ? "input-error" : ""}`}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Digite sua senha"
              />
              <div className="password-toggle" onClick={togglePasswordVisibility}>
                {showPassword ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_886_2866)">
                  <path d="M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 12C3.60014 7.90264 7.33603 5 12 5C16.664 5 20.3999 7.90264 22 12C20.3999 16.0974 16.664 19 12 19C7.33603 19 3.60014 16.0974 2 12Z" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </g>
                  <defs>
                  <clipPath id="clip0_886_2866">
                  <rect width="24" height="24" fill="white"/>
                  </clipPath>
                  </defs>
                  </svg>
              ) : (
                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_886_5106)">
                <path d="M11.2302 5.07319C11.6449 5.02485 12.0685 5 12.5 5C17.164 5 20.8999 7.90264 22.5 12C22.1054 13.0104 21.5809 13.9482 20.9447 14.7877M7.01962 6.51944C4.97955 7.76406 3.40111 9.69259 2.5 12C4.10014 16.0974 7.83603 19 12.5 19C14.5376 19 16.398 18.446 17.9806 17.4804M10.3788 9.87859C9.83582 10.4215 9.5 11.1715 9.5 12C9.5 13.6569 10.8431 15 12.5 15C13.3285 15 14.0785 14.6642 14.6214 14.1212" stroke="#4285F3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M4.5 4L20.5 20" stroke="#4285F3" stroke-width="2" stroke-linecap="round"/>
                </g>
                <defs>
                <clipPath id="clip0_886_5106">
                <rect width="24" height="24" fill="white" transform="translate(0.5)"/>
                </clipPath>
                </defs>
                </svg>
                )}
              </div>
            </div>
            {errors.password && (
              <div className="error-message">{errors.password}</div>
            )}
          </div>
        </div>

        {serverError && <div className="error-message">{serverError}</div>}

        <div className="remember-password" onClick={toggleRememberPassword}>
          <div className="checkbox-container">
          <input
            type="checkbox"
            name="rememberPassword"
            className="checkbox-container"
            checked={formData.rememberPassword}
            onChange={handleChange}
          />
          </div>
          <div className="remember-text">Lembrar senha</div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className={`login-button ${!isFormValid || loading ? "button-disabled" : ""}`}
            disabled={!isFormValid || loading}
          >
            {loading ? "Entrando..." : "Realizar login"}
          </button>
          <div className="signup-section">
            <div className="signup-text">Ainda não possui conta?</div>
            <button
              type="button"
              className="signup-link"
              onClick={() => navigate("/create-account")}
            >
              Criar conta
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default LoginForm;
